import { useState } from "react";
import Modal from "@mui/material/Modal";
import SupportIcon from "../../../Assets/Icons/SupportIcon";
import { TextField } from "@mui/material";
import MainButton from "../../Buttons/MainButton";
import ModalShell, { ModalTitle } from "../../ModalShell";

function FertilizerIngredientModal({
  open,
  handleClose,
  handleAddNewIngredient,
}) {
  const [name, setName] = useState("");
  const [nValue, setNValue] = useState(0);
  const [pValue, setPValue] = useState(0);
  const [kValue, setKValue] = useState(0);
  const [sValue, setSValue] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  function resetState() {
    setName("");
    setNValue(0);
    setPValue(0);
    setKValue(0);
    setSValue(0);
    setErrorMessage("");
    setIsSaving(false);
  }

  const nutrientValues = [nValue, pValue, kValue, sValue];
  const valuesAreValid = nutrientValues.every(
    (value) => Number.isFinite(value) && value >= 0 && value <= 100
  );
  const combinedPercentageIsValid =
    nutrientValues.reduce((total, value) => total + value, 0) <= 100;
  const isSaveDisabled =
    !name.trim() ||
    !valuesAreValid ||
    !combinedPercentageIsValid ||
    !nutrientValues.some((value) => value > 0) ||
    isSaving;

  async function handleSubmit() {
    setIsSaving(true);
    setErrorMessage("");
    try {
      await handleAddNewIngredient({
        name: name.trim(),
        n_value: nValue,
        p_value: pValue,
        k_value: kValue,
        s_value: sValue,
      });
      resetState();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setErrorMessage(
        typeof detail === "string"
          ? detail
          : "The fertilizer ingredient could not be saved."
      );
      setIsSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        resetState();
        handleClose(false);
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <ModalShell
        accent="info"
        icon={<SupportIcon color={"#FFFFFF"} width="38px" height="38px" />}
        onClose={() => {
          resetState();
          handleClose(false);
        }}
        width="min(620px, calc(100vw - 32px))"
      >
          <ModalTitle accent="info" className="text-[20px]">
            Add a new Ingredient
          </ModalTitle>

          <div className="grid w-full gap-4 md:grid-cols-2">
            <TextField
              label="Ingredient Name"
              sx={{ width: "100%" }}
              InputProps={{ inputProps: { min: 0 } }}
              variant="outlined"
              value={name}
              placeholder="Enter an Ingredient's name e.g 'Urea'"
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              label="Nitrogen"
              sx={{ width: "100%" }}
              type="number"
              InputProps={{ inputProps: { min: 0, max: 100 } }}
              variant="outlined"
              value={nValue}
              onChange={(e) => setNValue(parseFloat(e.target.value) || 0)}
            />
            <TextField
              label="Phosphorus"
              sx={{ width: "100%" }}
              type="number"
              InputProps={{ inputProps: { min: 0, max: 100 } }}
              variant="outlined"
              value={pValue}
              onChange={(e) => setPValue(parseFloat(e.target.value) || 0)}
            />
            <TextField
              label="Potassium"
              sx={{ width: "100%" }}
              type="number"
              InputProps={{ inputProps: { min: 0, max: 100 } }}
              variant="outlined"
              value={kValue}
              onChange={(e) => setKValue(parseFloat(e.target.value) || 0)}
            />
            <TextField
              label="Sulphur"
              sx={{ width: "100%" }}
              type="number"
              InputProps={{ inputProps: { min: 0, max: 100 } }}
              variant="outlined"
              value={sValue}
              onChange={(e) => setSValue(parseFloat(e.target.value) || 0)}
            />
          </div>
          {!valuesAreValid ? (
            <p className="mt-3 text-sm font-semibold text-red-700">
              Nutrient percentages must be between 0 and 100.
            </p>
          ) : null}
          {valuesAreValid && !combinedPercentageIsValid ? (
            <p className="mt-3 text-sm font-semibold text-red-700">
              Combined nutrient percentages cannot exceed 100.
            </p>
          ) : null}
          {valuesAreValid &&
          combinedPercentageIsValid &&
          !nutrientValues.some((value) => value > 0) ? (
            <p className="mt-3 text-sm font-semibold text-amber-800">
              Enter at least one nutrient percentage.
            </p>
          ) : null}
          {errorMessage ? (
            <p className="mt-3 text-sm font-semibold text-red-700" role="alert">
              {errorMessage}
            </p>
          ) : null}
          <div className="mt-6 flex w-full justify-center gap-4">
            <MainButton
              onClick={handleSubmit}
              text={isSaving ? "Saving..." : "Save"}
              disabled={isSaveDisabled}
            />
            <MainButton
              onClick={() => {
                resetState();
                handleClose(false);
              }}
              secondary={true}
              text="Cancel"
            />
          </div>
      </ModalShell>
    </Modal>
  );
}

export default FertilizerIngredientModal;
